import React from 'react';

import classNames from 'classnames';
import { faStop } from '@fortawesome/pro-solid-svg-icons';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import SanitizedHTML from 'components/ui/sanitized_html';

import ActionConfirmations from './action_confirmations';
import SparkBadge from './spark_badge';
import { AI_HTML_OPTIONS } from 'components/ai/html';
import { InlineTools } from './tools/inline';
import { StepCard, liveSteps } from './tools/inline/step_card';
import { formatMetrics } from './metrics/helpers';

import type { ActionVerdict, AssistantAction, Message } from './types';

interface Props {
  message: Message;
  resolveDisabled: boolean;
  showAdminDetails: boolean;
  showStopSpeaking: boolean;
  onResolveAction: (action: AssistantAction, verdict: ActionVerdict) => void;
  onStopSpeaking: () => void;
}

export default function AssistantMessage({
  message,
  resolveDisabled,
  showAdminDetails,
  showStopSpeaking,
  onResolveAction,
  onStopSpeaking,
}: Props) {
  const runningSteps = liveSteps(message.steps, message.inlineToolCalls);

  return (
    <div className="d-flex gap-2 mb-3">
      <SparkBadge className="flex-shrink-0 align-self-start" />
      <div className={classNames({ 'text-danger': message.error })} style={{ maxWidth: '85%' }}>
        <InlineTools toolCalls={message.inlineToolCalls || []} />
        {runningSteps && <StepCard steps={runningSteps} />}
        {message.content && (
          <SanitizedHTML
            className={classNames('ai-assistant-prose', {
              'is-streaming': message.isStreaming && !message.status,
            })}
            html={message.content}
            {...AI_HTML_OPTIONS}
          />
        )}
        {message.actions && message.actions.length > 0 && (
          <ActionConfirmations actions={message.actions} disabled={resolveDisabled} onResolve={onResolveAction} />
        )}
        {message.isStreaming && !runningSteps && (message.status || !message.content) && (
          <div className="d-flex align-items-center gap-2 text-muted small mt-1">
            <LoadingSpinner inline size="sm" />
            <span className="fst-italic">{message.status || 'Thinking'}…</span>
          </div>
        )}
        {showStopSpeaking && (
          <Button
            aria-label="Stop speaking"
            className="text-danger mt-1"
            icon={faStop}
            size="sm"
            variant="ghost"
            onClick={onStopSpeaking}
          />
        )}
        {showAdminDetails && message.metrics && (
          <div className="text-muted small mt-1">{formatMetrics(message.metrics)}</div>
        )}
      </div>
    </div>
  );
}
